
import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useBeforeUnload, useLocation, useNavigate } from "react-router-dom";

// Custom hook to handle navigation blocking
export function usePromptIfDirty(isDirty, message = "You have unsaved changes. Are you sure you want to leave?") {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPrompt, setShowPrompt] = useState(false);
  const [lastLocation, setLastLocation] = useState(null);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);

  // Handle browser beforeunload event (refreshing or closing tab)
  useBeforeUnload(
    isDirty ? (event) => {
      event.preventDefault();
      return message;
    } : undefined
  );

  // Store the current pathname in a ref so we can track when it changes
  useEffect(() => {
    if (!isDirty) {
      return;
    }

    // Handle navigation within the React app
    const handleBeforeNavigate = (e) => {
      const currentPath = location.pathname;
      // Only trigger prompt if pathname is changing (not just search params or hash)
      const targetPath = (e.currentTarget?.pathname || e.detail?.to || "").toString();

      if (targetPath && targetPath !== currentPath) {
        e.preventDefault();
        setLastLocation({ pathname: targetPath });
        setShowPrompt(true);
      }
    };

    // Add event listeners to relevant navigation elements
    const selectors = 'a[href]:not([target="_blank"]), button[type="submit"]';
    const elements = document.querySelectorAll(selectors);
    elements.forEach(el => el.addEventListener('click', handleBeforeNavigate));

    // Custom event we can dispatch when programmatic navigation occurs
    window.addEventListener('beforeNavigate', handleBeforeNavigate);

    return () => {
      elements.forEach(el => el.removeEventListener('click', handleBeforeNavigate));
      window.removeEventListener('beforeNavigate', handleBeforeNavigate);
    };
  }, [isDirty, location, navigate]);

  // Handle confirmed navigation
  useEffect(() => {
    if (confirmedNavigation && lastLocation) {
      navigate(lastLocation.pathname);
      setConfirmedNavigation(false);
      setLastLocation(null);
    }
  }, [confirmedNavigation, lastLocation, navigate]);

  // Return values and handlers to be used in the component
  return {
    showPrompt,
    confirmNavigation: () => setConfirmedNavigation(true),
    cancelNavigation: () => {
      setShowPrompt(false);
      setLastLocation(null);
    }
  };
}

// Component that uses the custom hook
const NavigationPrompt = ({ when, message, children }) => {
  const { showPrompt, confirmNavigation, cancelNavigation } = usePromptIfDirty(when, message);

  return (
    <>
      {children}
        <Modal
            show={showPrompt} 
            onHide={cancelNavigation}
            size="md"
            aria-labelledby="contained-modal-title-vcenter"
            centered
            >
            <Modal.Header closeButton style={{border:"none"}}>
                <h4 className='mb-0'>Leave Page</h4>
            </Modal.Header>
            <Modal.Body>
            <div className="d-flex w-100 flex-column modal_register">
                <h5>Do you want to save changes before leaving this page?</h5>
                <p>Your changes will be lost if you leave now.</p>
            </div>
            </Modal.Body>
            <Modal.Footer>
            <button className='blue_primarymodalbtn1' onClick={cancelNavigation}>
                Cancel
            </button>
            <button className='blue_primarymodalbtn2' onClick={confirmNavigation}>
                Leave
            </button>
            </Modal.Footer>
      </Modal>
    </>
  );
};

export default NavigationPrompt;